import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import moment from "moment";
import ReactPaginate from "react-paginate";
import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

import Sidebar from "../sidebar";
import {
  checkImageExist,
  deleteImages,
  deleteStockProduct,
  downloadSample,
  getStockProducts,
  importStockProducts,
} from "./stock-products-section.service";
import toast from "react-hot-toast";
import Toggle from "react-toggle";
import { updateStockProduct } from "./add-and-edit-stock-product-page/form-section.service";
import Swal from "sweetalert2";
import { AuthContext } from "../../../contexts/AuthContext";

export default function StockProductsSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageParam = queryParams.get("page");
  const searchParam = queryParams.get("search");
  const inputFile = useRef<HTMLInputElement>(null);
  const [currentPage, setCurrentPage] = useState(
    pageParam ? parseInt(pageParam) : 1
  );
  const [search, setSearch] = useState(searchParam || "");
  const queryClient = useQueryClient();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(parseInt(pageParam));
    }
  }, [pageParam]);

  const {
    isPending,
    isError,
    data: response,
    error,
  } = useQuery({
    queryKey: ["admin-stock-products", currentPage, search],
    queryFn: () => getStockProducts({ page: currentPage, name: search }),
    placeholderData: keepPreviousData,
  });

  const handlePageChange = (selectedItem: { selected: number }) => {
    const newPage = selectedItem.selected + 1;
    setCurrentPage(newPage);
    navigate(`?page=${newPage}`);
  };

  const handleDeleteStockProduct = async (stockProductId: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      await deleteStockProduct(stockProductId);
      queryClient.invalidateQueries({ queryKey: ["admin-stock-products"] });
      Swal.fire("Deleted!", "The stock product has been deleted.", "success");
    }
  };

  const handleDownloadSample = async () => {
    await downloadSample().then((response) => {
      const data = new Uint8Array(response.data.data);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `stock-products-${moment().format()}.xlsx`);
      document.body.appendChild(link);
      link.click();
    });
  };

  const handleImportFile = async (event: any) => {
    console.log(event.target.files[0]);
    const formData = new FormData();
    formData.append("file", event.target.files[0]);
    await importStockProducts(formData).then((response) => {
      toast.success(response?.data.message);
      queryClient.invalidateQueries({ queryKey: ["admin-stock-products"] });
    });
  };

  const handleCheckImages = async () => {
    checkImageExist().then((response) => {
      const data = new Uint8Array(response.data.data);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `product-image-not-exist-${moment().format()}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
    });
  };

  const handleToggleStockProduct = async (
    stockProductId: string,
    isActive: boolean
  ) => {
    await updateStockProduct(stockProductId, {
      isActive: !isActive,
    });
    queryClient.invalidateQueries({ queryKey: ["admin-stock-products"] });
  };

  const isBackendUser = user?.userType === "Back end Entry User";
  const isSuperAdmin = user?.userType === "Super Admin";

  return (
    <section className="ec-page-content ec-vendor-dashboard section-space-p">
      <div className="container">
        <div className="row">
          <Sidebar />
          <div className="ec-shop-rightside col-lg-9 col-md-12">
            <div className="ec-vendor-dashboard-card space-bottom-30">
              <div className="ec-vendor-card-header">
                <h5>Stock Products</h5>
                <div className="ec-header-btn">
                  <button
                    onClick={handleCheckImages}
                    className="btn btn-lg btn-primary"
                  >
                    Check Images Exist
                  </button>

                  <button
                    className="btn btn-lg btn-primary"
                    onClick={() => {
                      if (inputFile.current) {
                        inputFile.current.click();
                      }
                    }}
                  >
                    Import
                  </button>
                  <input
                    ref={inputFile}
                    type="file"
                    hidden
                    onChange={handleImportFile}
                    onClick={(event: any) => {
                      event.target.value = null;
                    }}
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  />
                  <button
                    className="btn btn-lg btn-primary"
                    onClick={handleDownloadSample}
                  >
                    Download Sample
                  </button>
                  <Link className="btn btn-lg btn-primary" to="add">
                    Add Stock Product
                  </Link>
                </div>
              </div>
              <div className="ec-vendor-card-header p-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search products..."
                  onChange={(e) => {
                    const searchQuery = e.target.value;
                    navigate(`?page=1&search=${searchQuery}`);
                    setSearch(searchQuery);
                  }}
                />
              </div>
              <div className="ec-vendor-card-body">
                <div className="ec-vendor-card-table">
                  <table className="table ec-table">
                    <thead>
                      <tr>
                        <th scope="col">Image</th>
                        <th scope="col">Product Code</th>
                        <th scope="col">Product Name</th>
                        <th scope="col">Category</th>
                        <th scope="col">Stock Quantity</th>
                        <th scope="col">Status</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {response?.data.results.map((product: any) => (
                        <tr key={product.id}>
                          <td>
                            {product?.image?.key ? (
                              <img
                                src={`https://d1gdand68muq63.cloudfront.net/products/96/${product.image.key}`}
                                alt={product.name}
                                width="80"
                              />
                            ) : (
                              <span>No Image Available</span>
                            )}
                          </td>
                          <td>
                            <span>{product.code}</span>
                          </td>
                          <td>
                            <span>{product.name}</span>
                          </td>
                          <td>
                            <span>{product.category}</span>
                          </td>
                          <td>
                            <span>{product.quantity}</span>
                          </td>
                          <td>
                            <span>
                              <Toggle
                                defaultChecked={product.isActive}
                                icons={false}
                                onChange={() => {
                                  handleToggleStockProduct(
                                    product.id,
                                    product.isActive
                                  );
                                }}
                              />
                            </span>
                          </td>
                          <td>
                            <span>
                              <Link
                                to={`edit/${product.id}`}
                                className="ec-action-btn mr-1"
                              >
                                <i className="fa fa-edit fs-5"></i>
                              </Link>
                              {isSuperAdmin && (
                                <Link
                                  onClick={() => {
                                    handleDeleteStockProduct(product.id);
                                  }}
                                  to={""}
                                  className="ec-action-btn text-danger "
                                >
                                  <i className="fa fa-trash fs-5"></i>
                                </Link>
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="col-lg-12">
                  <ReactPaginate
                    nextLabel=">>"
                    onPageChange={handlePageChange}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={2}
                    pageCount={response?.data.totalPages}
                    forcePage={currentPage - 1}
                    previousLabel="<<"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    breakLabel="..."
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                    containerClassName="pagination"
                    activeClassName="active"
                    renderOnZeroPageCount={null}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
